import type { IconName } from "../design/icons";
import type { Role, Surface } from "../api/types";

export type NavItem = {
  key: string;
  name: string;
  to: string;
  icon: IconName;
  roles: Role[];
  badge?: "entityCount";
  disabled?: boolean;
  /** Key of the item this one is nested under; drawn indented beneath it. */
  parent?: string;
};

export type NavGroup = { label: string; items: NavItem[] };

export const DISABLED_HINT = "Available in a later release";

const GROUP_ADMIN: Role[] = ["group_admin"];
// m4-rulings.md #68: an entity user reaches In flight and Landed; Held is gated inside the page.
const INBOUND_ROLES: Role[] = ["group_admin", "entity_admin", "finance"];
const ENTITY_ROLES: Role[] = ["entity_admin", "finance", "readonly"];

export const HUB_NAV: NavGroup[] = [
  {
    label: "Operations",
    items: [
      { key: "dashboard", name: "Group Dashboard", to: "/", icon: "grid", roles: GROUP_ADMIN },
      { key: "documents", name: "Documents", to: "/documents", icon: "queue", roles: GROUP_ADMIN },
      { key: "inbound", name: "Inbound Documents", to: "/inbound", icon: "inbox", roles: INBOUND_ROLES },
    ],
  },
  {
    label: "Records",
    items: [{ key: "reports", name: "Reports", to: "/reports", icon: "chart", roles: GROUP_ADMIN }],
  },
  {
    label: "Configuration",
    items: [
      { key: "companies", name: "Companies", to: "/companies", icon: "companies", roles: GROUP_ADMIN, badge: "entityCount" },
      { key: "onboard", name: "Onboard a Company", to: "/companies/onboard", icon: "plus", roles: GROUP_ADMIN, parent: "companies" },
      { key: "mapping", name: "Mapping Studio", to: "/mapping", icon: "map", roles: GROUP_ADMIN, disabled: true },
      { key: "users", name: "Users & Access", to: "/users", icon: "users", roles: GROUP_ADMIN },
      { key: "settings", name: "Settings", to: "/settings", icon: "shield", roles: GROUP_ADMIN },
      { key: "theme", name: "Theme Settings", to: "/settings/theme", icon: "settings", roles: GROUP_ADMIN },
    ],
  },
];

// m6-rulings.md #131: the portal shell; every path stays under /portal.
export const PORTAL_NAV: NavGroup[] = [
  {
    label: "Your company",
    items: [
      { key: "overview", name: "Overview", to: "/portal", icon: "grid", roles: ENTITY_ROLES },
      { key: "documents", name: "Documents", to: "/portal/documents", icon: "queue", roles: ENTITY_ROLES },
      { key: "users", name: "Users", to: "/portal/users", icon: "users", roles: ["entity_admin"] },
    ],
  },
];

/** The one item a path lights: the longest `to` the path sits under. A child route lights
    only its own entry, and a detail page (/companies/:id) still lights its parent (BUG-013). */
export function activeNavKey(groups: NavGroup[], pathname: string): string | null {
  let best: NavItem | null = null;
  for (const item of groups.flatMap((group) => group.items)) {
    if (item.disabled) continue;
    const hit = item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (hit && (!best || item.to.length > best.to.length)) best = item;
  }
  return best?.key ?? null;
}

export function visibleNav(groups: NavGroup[], role: Role): NavGroup[] {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.roles.includes(role)) }))
    .filter((group) => group.items.length > 0);
}

/** A portal principal is never handed a hub entry, whatever the role says. */
export function navFor(surface: Surface, role: Role): NavGroup[] {
  return visibleNav(surface === "portal" ? PORTAL_NAV : HUB_NAV, role);
}
